import { inject, injectable } from 'inversify'
import type { JsonType } from 'posthog-js'
import { SERVICE_TYPES } from '@/core/container/types'
import type { PostHogService } from '@/services/tracking/postHogService'
import type { PostHogFeatureFlag } from '@/services/tracking/types'
import { isTest, isProd } from '@/utils/system'
import type { MonitoringLoggerService } from '@/services/monitoring/monitoringLoggerService'
import { LogLevel } from '@/core/logger/types'

export type FeatureFlagOverrides =
  | boolean
  | string[]
  | Record<string, string | boolean>
  | {
      flags?: Record<string, string | boolean>
      payloads?: Record<string, JsonType>
    }

const WAIT_FOR_FEATURE_FLAGS_TIMEOUT = 5_000

@injectable()
export class PostHogFeatureFlagsService {
  constructor(
    @inject(SERVICE_TYPES.PostHogService) protected readonly postHogService: PostHogService,
    @inject(SERVICE_TYPES.MonitoringLoggerService) protected readonly monitoringService: MonitoringLoggerService,
  ) {}

  public isFeatureEnabled(featureFlag: PostHogFeatureFlag): boolean {
    try {
      return Boolean(this.postHogService.getPosthog().isFeatureEnabled(featureFlag))
    } catch (e) {
      this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)

      return false
    }
  }

  public getFeatureFlagVariant(featureFlag: PostHogFeatureFlag): boolean | string | undefined {
    try {
      return this.postHogService.getPosthog().getFeatureFlag(featureFlag)
    } catch (e) {
      this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)

      return undefined
    }
  }

  public getFeatureFlagPayload(featureFlag: PostHogFeatureFlag): JsonType {
    try {
      return this.postHogService.getPosthog().getFeatureFlagPayload(featureFlag)
    } catch (e) {
      this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)
      return undefined
    }
  }

  public reloadFeatureFlags(): void {
    try {
      this.postHogService.getPosthog().reloadFeatureFlags()
    } catch (e) {
      this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)
    }
  }

  public get hasLoadedFlags(): boolean {
    return this.postHogService.isInit && this.postHogService.getPosthog().featureFlags.hasLoadedFlags
  }

  public waitForFeatureFlags(): Promise<void> {
    const { promise, resolve } = Promise.withResolvers<void>()

    if (!this.isSupportedEnvironment() || this.postHogService.getPosthog().featureFlags.hasLoadedFlags) {
      resolve()
    } else {
      try {
        const unsubscribe = this.postHogService.getPosthog().onFeatureFlags(() => {
          clearTimeout(timeout)
          unsubscribe()
          resolve()
        })

        const timeout = setTimeout(() => {
          unsubscribe()
          resolve()
          this.monitoringService.logAndCapture('waitForFeatureFlags timed out', LogLevel.ERROR)
        }, WAIT_FOR_FEATURE_FLAGS_TIMEOUT)
      } catch (e) {
        this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)
        resolve()
      }
    }

    return promise
  }

  public overrideFeatureFlags(overrides: FeatureFlagOverrides): void {
    try {
      this.postHogService.getPosthog().featureFlags.overrideFeatureFlags(overrides)
    } catch (e) {
      this.monitoringService.logAndCapture(e instanceof Error ? e : 'Unknown error', LogLevel.ERROR)
    }
  }

  public resetFeatureFlagOverrides(): void {
    this.overrideFeatureFlags(false)
  }

  protected isSupportedEnvironment(): boolean {
    return isTest || isProd
  }
}
